import React from 'react';
import { formatMoney } from '../../utils/money';
import styles from './CustomTooltip.module.css';

interface TooltipPayload {
  name?: string;
  value?: number;
  color?: string;
  payload?: Record<string, unknown>;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: unknown[];
  label?: string;
  formatDate?: (date: string) => string;
  type?: 'line' | 'area';
  assetNames?: Record<string, string>;
}

export const CustomTooltip: React.FC<CustomTooltipProps> = ({
  active,
  payload,
  label,
  formatDate,
  type = 'line',
  assetNames = {}
}) => {
  if (!active || !payload || !payload.length || !label) {
    return null;
  }

  const formattedDate = formatDate ? formatDate(label) : label;

  return (
    <div className={styles.tooltipContainer}>
      <div className={styles.tooltipDate}>{formattedDate}</div>
      <div className={styles.tooltipContent}>
        {payload.map((entryRaw: unknown, index: number) => {
          const entry = entryRaw as TooltipPayload;
          let displayName = entry.name || 'Unknown';
          let displayValue: string;
          
          if (type === 'line') {
            const labels: {[key: string]: string} = {
              total_value: 'Portfolio Value',
              total_invested: 'Total Invested',
              gain_loss_usd: 'Profit/Loss',
              gain_loss_percentage: 'ROI, %'
            };
            displayName = labels[entry.name || ''] || entry.name || 'Unknown';
            displayValue = entry.name === 'gain_loss_percentage' 
              ? `${(entry.value || 0).toFixed(2)}%` 
              : formatMoney(entry.value || 0);
          } else if (type === 'area') {
            // For area chart - handle asset names
            if (entry.name === 'asset_value_other') {
              displayName = 'Other Assets';
            } else {
              const assetIdMatch = entry.name?.match(/asset_value_(.+)/);
              if (assetIdMatch && assetIdMatch[1]) {
                const assetId = assetIdMatch[1];
                displayName = assetNames[assetId] || `Project ${index + 1}`;
              }
            }
            displayValue = formatMoney(entry.value || 0);
          } else {
            displayValue = String(entry.value || 0);
          }

          return (
            <div key={index} className={styles.tooltipItem}>
              <div 
                className={styles.tooltipItemColor}
                style={{ backgroundColor: entry.color }}
              />
              <span className={styles.tooltipItemLabel}>{displayName}:</span>
              <span className={styles.tooltipItemValue}>{displayValue}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}; 